import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { flows, TimeoutError } from '@repletoryreach/sdk-api';
import { Stack, Group, Text, Button, Badge } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { Textarea, Select } from '@repletoryreach/sdk-ui/inputs';
import { useI18n, useT } from '../../../../i18n/I18nProvider';
import { FileUpload } from '../../../../components/FileUpload';
import { ReplyHtml } from '../../../../app/assistant/ReplyHtml';
import '../../../../app/assistant/assistant.css';
import type { FlowDefinition } from '../mapper';

/**
 * "Build with AI": the chat that writes the flow.
 *
 * The user says what they want; the assistant answers in plain words and,
 * when it knows enough, with a whole flow that lands on the canvas at once
 * (one undo step, unsaved until Save). When it needs something from outside
 * the flow it says so, and the upload or the link is right here in the chat:
 * a document for an AI answer step, an approved template, a number, an API.
 * The server keeps no chat state; the transcript lives in this component and
 * in sessionStorage, and every turn carries the flow as it stands, so
 * changing an existing flow is the same conversation.
 */
type Turn = {
  role: 'user' | 'assistant';
  content: string;
  definition?: FlowDefinition | null;
  changes?: string[];
  questions?: string[];
  needs?: flows.AssistantNeed[];
  suggestions?: string[];
  problems?: string[];
  provider?: string;
  applied?: boolean;
  failed?: boolean;
};

const MAX_TURNS_SENT = 24;

/**
 * A whole flow written into a message (19 October 2026): a flow the user pasted, or one the assistant wrote out as
 * text. Every JSON object in the text is tried; the first with steps (and a trigger or connections) is the flow.
 */
export function findFlow(text: string): FlowDefinition | null {
  let depth = 0, start = -1, inString = false, escaped = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (c === '\\') escaped = true;
      else if (c === '"') inString = false;
      continue;
    }
    if (c === '"' && depth > 0) { inString = true; continue; }
    if (c === '{') { if (depth++ === 0) start = i; continue; }
    if (c !== '}' || depth === 0) continue;
    if (--depth > 0) continue;
    try {
      const value = JSON.parse(text.slice(start, i + 1)) as Record<string, unknown>;
      const found = value && typeof value === 'object' && Array.isArray(value.definition && (value.definition as Record<string, unknown>).nodes)
        ? value.definition as Record<string, unknown> : value;
      if (Array.isArray(found.nodes) && found.nodes.length > 0 && ('trigger' in found || 'edges' in found)) return found as FlowDefinition;
    } catch { /* braces in prose, not JSON */ }
  }
  return null;
}

export function AssistantPanel({
  opened, onClose, flowId, flowName, definition, onApply, onUndo
}: {
  opened: boolean;
  onClose: () => void;
  flowId: string;
  flowName: string;
  /** The flow as it stands on the canvas. */
  definition: FlowDefinition;
  /** Put a proposed flow on the canvas (one undo step). */
  onApply: (definition: FlowDefinition) => void;
  onUndo: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const storageKey = `rr.flow-assistant.${flowId}`;
  const [turns, setTurns] = useState<Turn[]>(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as Turn[]) : [];
    } catch { return []; }
  });
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  // A bigger flow takes a while to write (19 October 2026): after a few seconds, say so, so nobody gives up on it.
  const [slow, setSlow] = useState(false);
  // Which model writes the answers (19 October 2026): the standard one, or a stronger one that costs more credit per
  // message. Remembered in this browser; offered only when a stronger one is switched on.
  const models = useQuery({ queryKey: ['flows', 'assistant-models'], queryFn: () => flows.assistModels(), staleTime: 300_000, retry: false });
  const [model, setModel] = useState<string>(() => { try { return localStorage.getItem('rr.flow-assistant.model') ?? ''; } catch { return ''; } });
  const available = models.data?.data ?? [];
  const chosen = available.find((m) => m.code === model && !m.is_default) ?? null;
  const pick = (code: string) => {
    setModel(code);
    try { localStorage.setItem('rr.flow-assistant.model', code); } catch { /* storage may be off */ }
  };
  const logRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    try { sessionStorage.setItem(storageKey, JSON.stringify(turns.slice(-60))); } catch { /* storage may be off */ }
  }, [turns, storageKey]);
  useEffect(() => {
    if (opened) logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [turns, busy, opened]);

  const starters = useMemo(() => [t('flows.assistant.starter1'), t('flows.assistant.starter2'), t('flows.assistant.starter3')], [t]);
  const hasSteps = Array.isArray(definition.nodes) && definition.nodes.length > 0;

  const send = useCallback(async (text: string) => {
    const content = text.trim();
    if (!content || busy) return;
    const next: Turn[] = [...turns, { role: 'user', content }];
    setTurns(next);
    setDraft('');
    setBusy(true);
    const slowTimer = setTimeout(() => setSlow(true), 8000);
    try {
      const res = await flows.assist({
        messages: next.slice(-MAX_TURNS_SENT).map((x) => ({ role: x.role, content: x.content })),
        definition: hasSteps ? definition : undefined,
        flow_id: flowId,
        locale,
        // Formatted like the console assistant (14 October 2026): simple HTML, never Markdown.
        format: 'html',
        model: chosen?.code
      });
      const d = res.data;
      const proposal = d.definition && typeof d.definition === 'object' ? (d.definition as FlowDefinition) : null;
      if (proposal) onApply(proposal);
      setTurns((cur) => [...cur, {
        role: 'assistant',
        content: d.reply,
        definition: proposal,
        changes: d.changes,
        questions: d.questions,
        needs: d.needs,
        suggestions: d.suggestions,
        problems: d.problems,
        provider: d.provider,
        applied: !!proposal
      }]);
    } catch (e) {
      const said = e instanceof TimeoutError ? t('flows.assistant.timeout') : e instanceof Error ? e.message : t('flows.assistant.error');
      setTurns((cur) => [...cur, { role: 'assistant', content: said, failed: true }]);
    } finally {
      clearTimeout(slowTimer);
      setSlow(false);
      setBusy(false);
    }
  }, [busy, turns, definition, hasSteps, flowId, locale, onApply, t, chosen]);

  // "Put on canvas" (19 October 2026): any flow in the conversation, an earlier version or one pasted in, onto the canvas.
  const putOnCanvas = useCallback((index: number, flow: FlowDefinition) => {
    onApply(flow);
    setTurns((cur) => cur.map((x, i) => (i === index ? { ...x, applied: true } : x)));
  }, [onApply]);

  const lastBot = [...turns].reverse().find((x) => x.role === 'assistant');
  const stub = lastBot?.provider === 'stub';

  return (
    <Drawer opened={opened} onClose={onClose} title={t('flows.assistant.title')} position="right" size="lg">
      <div className="rr-assist">
        <div className="rr-assist__log" ref={logRef} aria-live="polite">
          <div className="rr-assist__msg rr-assist__msg--bot">
            <Text role="bodySmall">{hasSteps ? t('flows.assistant.welcomeExisting', { name: flowName }) : t('flows.assistant.welcome')}</Text>
            {turns.length === 0 && (
              <div className="rr-assist__chips">
                {starters.map((s) => <button key={s} type="button" className="rr-assist__chip" onClick={() => send(s)}>{s}</button>)}
              </div>
            )}
          </div>
          {turns.map((turn, i) => {
            const flow = turn.definition ?? findFlow(turn.content);
            return (
              <div key={i} className={`rr-assist__msg rr-assist__msg--${turn.role === 'user' ? 'user' : 'bot'}${turn.failed ? ' is-failed' : ''}`}>
                {turn.role === 'assistant' && !turn.failed
                  ? <div className="rr-assist__reply"><ReplyHtml html={turn.content} /></div>
                  : <Text role="bodySmall" style={{ whiteSpace: 'pre-wrap' }}>{turn.content}</Text>}
                {turn.role === 'assistant' && <Extras turn={turn} onSay={send} onUndo={onUndo} onPut={flow ? () => putOnCanvas(i, flow) : undefined} />}
                {turn.role === 'user' && flow && (
                  <Group gap="xs" mt={6}>
                    <Button size="xs" variant="default" onClick={() => putOnCanvas(i, flow)} data-testid="assist-put">{t('flows.assistant.putOnCanvas')}</Button>
                  </Group>
                )}
              </div>
            );
          })}
          {busy && (
            <div className="rr-assist__msg rr-assist__msg--bot is-busy" data-testid="assist-busy">
              <Text role="caption">{t('flows.assistant.thinking')}</Text>
              {slow && <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.assistant.slow')}</Text>}
            </div>
          )}
        </div>
        <form
          className="rr-assist__composer"
          onSubmit={(e) => { e.preventDefault(); void send(draft); }}
        >
          <Textarea
            aria-label={t('flows.assistant.placeholder')}
            placeholder={t('flows.assistant.placeholder')}
            description={t('flows.assistant.keptNotice')}
            value={draft}
            minRows={2}
            maxRows={6}
            onChange={(e) => setDraft(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(draft); }
            }}
          />
          {available.length > 1 && (
            <Select
              label={t('flows.assistant.model')}
              description={chosen
                ? (chosen.cost_factor ? t('flows.assistant.modelCost', { factor: String(chosen.cost_factor).replace('.', ',') }) : t('flows.assistant.modelCostUnknown'))
                : t('flows.assistant.modelStandardHint')}
              value={chosen?.code ?? available.find((m) => m.is_default)?.code ?? ''}
              onChange={(e) => pick(e.currentTarget.value)}
              disabled={busy}
              data={available.map((m) => ({ value: m.code, label: m.is_default ? t('flows.assistant.modelStandard', { name: m.name }) : t('flows.assistant.modelStronger', { name: m.name }) }))}
              data-testid="assist-model"
            />
          )}
          <Group justify="space-between" align="center">
            <Group gap="xs">
              <Button size="xs" variant="subtle" disabled={turns.length === 0 || busy} onClick={() => setTurns([])}>{t('flows.assistant.clear')}</Button>
              {stub && <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.assistant.stubHint')} <Link to="/settings">{t('nav.settings')}</Link></Text>}
            </Group>
            <Button size="sm" type="submit" disabled={!draft.trim()} loading={busy}>{t('flows.assistant.send')}</Button>
          </Group>
        </form>
      </div>
    </Drawer>
  );
}

/** What came with an answer: the changes made, open questions, needs, suggestions, problems. */
function Extras({ turn, onSay, onUndo, onPut }: { turn: Turn; onSay: (text: string) => void; onUndo: () => void; onPut?: () => void }) {
  const t = useT();
  const needs = turn.needs ?? [];
  const hasAny = turn.applied || !!onPut || (turn.changes?.length ?? 0) > 0 || (turn.questions?.length ?? 0) > 0 || needs.length > 0 || (turn.suggestions?.length ?? 0) > 0 || (turn.problems?.length ?? 0) > 0;
  if (!hasAny) return null;
  return (
    <Stack gap={8} className="rr-assist__extras">
      {(turn.applied || onPut) && (
        <Group gap="xs" align="center">
          {turn.applied && <Badge size="sm" variant="light" color="success">{t('flows.assistant.applied')}</Badge>}
          {turn.applied && <Button size="xs" variant="subtle" onClick={onUndo}>{t('flows.assistant.undo')}</Button>}
          {/* An earlier version, or a flow the assistant wrote out as text, can be put on the canvas again. */}
          {onPut && <Button size="xs" variant="default" onClick={onPut} data-testid="assist-put">{t(turn.applied ? 'flows.assistant.putAgain' : 'flows.assistant.putOnCanvas')}</Button>}
        </Group>
      )}
      {(turn.changes?.length ?? 0) > 0 && (
        <div>
          <Text role="caption" fw={700}>{t('flows.assistant.changes')}</Text>
          <ul className="rr-assist__list">{turn.changes!.map((c, i) => <li key={i}>{c}</li>)}</ul>
        </div>
      )}
      {(turn.problems?.length ?? 0) > 0 && (
        <div className="rr-assist__problems">
          <Text role="caption" fw={700}>{t('flows.assistant.problems')}</Text>
          <ul className="rr-assist__list">{turn.problems!.map((c, i) => <li key={i}>{c}</li>)}</ul>
        </div>
      )}
      {needs.length > 0 && (
        <div>
          <Text role="caption" fw={700}>{t('flows.assistant.needs')}</Text>
          <Stack gap={6}>
            {needs.map((n, i) => <Need key={i} need={n} onSay={onSay} />)}
          </Stack>
        </div>
      )}
      {(turn.suggestions?.length ?? 0) > 0 && (
        <div>
          <Text role="caption" fw={700}>{t('flows.assistant.suggestions')}</Text>
          <div className="rr-assist__chips">
            {turn.suggestions!.map((s) => <button key={s} type="button" className="rr-assist__chip" onClick={() => onSay(s)}>{s}</button>)}
          </div>
        </div>
      )}
    </Stack>
  );
}

/** One thing the assistant needs, with the way to give it right here. */
function Need({ need, onSay }: { need: flows.AssistantNeed; onSay: (text: string) => void }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const kind = (['document', 'template', 'number', 'api', 'info'] as const).includes(need.kind as never) ? need.kind : 'info';
  return (
    <div className={`rr-assist__need rr-assist__need--${kind}`}>
      <Text role="bodySmall" fw={650}>{t(`flows.assistant.need.${kind}`)}</Text>
      <Text role="caption" c="var(--rr-color-text-muted)">{need.why}</Text>
      {kind === 'document' && (
        <Stack gap={4}>
          <Text role="caption">{t('flows.assistant.uploadHint')}</Text>
          <FileUpload
            accept={['application/pdf', 'text/plain', 'text/markdown', 'text/html', 'text/csv', '.pdf', '.txt', '.md', '.html', '.csv']}
            value={null}
            withLibrary={false}
            onChange={async (next) => {
              if (!next) return;
              setBusy(true);
              setError(null);
              try {
                const res = await flows.ingestKbFromMedia(next.id, next.original_name ?? undefined);
                onSay(t('flows.assistant.uploaded', { name: res.data.name, id: res.data.id }));
              } catch (e) {
                setError(e instanceof Error ? e.message : t('common.unknownError'));
              } finally {
                setBusy(false);
              }
            }}
          />
          {busy && <Text role="caption" c="var(--rr-color-text-muted)">{t('knowledge.add.reading')}</Text>}
          {error && <Text role="caption" c="var(--rr-color-error, #dc2626)">{error}</Text>}
          <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.assistant.orKnowledge')} <Link to="/knowledge">{t('nav.knowledge')}</Link></Text>
        </Stack>
      )}
      {kind === 'template' && <Link to="/templates" className="rr-connect__link">{t('nav.templates')}</Link>}
      {kind === 'number' && <Link to="/numbers" className="rr-connect__link">{t('nav.numbers')}</Link>}
      {kind === 'api' && <Text role="caption">{t('flows.assistant.apiHint')}</Text>}
    </div>
  );
}
